import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Home, Map, MessageSquare, Download, Settings, WifiOff } from "lucide-react";
import { useEffect, useState } from "react";

const TABS = [
  { to: "/", icon: Home, label: "Home" },
  { to: "/map", icon: Map, label: "Map" },
  { to: "/chat", icon: MessageSquare, label: "Blu" },
  { to: "/downloads", icon: Download, label: "Downloads" },
  { to: "/settings", icon: Settings, label: "Settings" },
] as const;

function OfflineBar() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);
  if (online) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      background: 'var(--orange)', color: '#fff',
      fontSize: 11, fontWeight: 600, padding: '7px 0', letterSpacing: '0.04em',
    }}>
      <WifiOff size={10} />
      OFFLINE — data saved locally
    </div>
  );
}

export function Shell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const isMap = location.pathname === "/map";

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: 'var(--bg)', overflow: 'hidden' }}>
      <OfflineBar />

      <main style={{ flex: 1, minHeight: 0, overflow: isMap ? 'hidden' : 'auto' }}>
        {children}
      </main>

      {/* Bottom nav */}
      <nav
        aria-label="Main navigation"
        style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          paddingTop: 8,
          paddingBottom: 'max(env(safe-area-inset-bottom, 8px), 8px)',
          background: 'rgba(8, 8, 8, 0.94)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderTop: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        {TABS.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            end={to === "/"}
            style={{ textDecoration: 'none' }}
          >
            {({ isActive }) => (
              <div style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                gap: 3, padding: '4px 14px', minWidth: 56, position: 'relative',
              }}>
                {isActive && (
                  <div style={{
                    position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)',
                    width: 4, height: 4, borderRadius: '50%', background: '#4cd964',
                  }} />
                )}
                <Icon
                  size={23}
                  strokeWidth={isActive ? 2.5 : 1.8}
                  color={isActive ? '#4cd964' : '#636366'}
                />
                <span style={{
                  fontSize: 10, fontWeight: 500,
                  color: isActive ? '#4cd964' : '#636366',
                  letterSpacing: '0.02em',
                }}>
                  {label}
                </span>
              </div>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
